import { useSyncExternalStore } from 'react'

// Whether the universal search (nav v2) is open, and in which mode. A module
// store so the header trigger, the keyboard shortcuts and the panel share one
// answer without a provider.

export type SearchMode = 'find' | 'ask'

/** What the search card sheet knows about a card before its detail read. */
export interface SheetCard {
  /** null when the caller has no oracle id: the sheet reads the card by name. */
  oracle_id: string | null
  name: string
  type_line: string | null
  image_url: string | null
  usd: number | null
  /** Lowest nonfoil USD across every printing (suggest rows only), else null. */
  usd_from: number | null
  /** Set by a card NAME opening the sheet (CardName): the sheet then shows
   *  the per-card buy link that name always led to, logged under `source`.
   *  Absent on the search's own rows, whose sheet is unchanged. */
  buy?: { source: string; tcgplayer_id: number | null }
}

export interface SearchOpenState {
  open: boolean
  mode: SearchMode
  /** Set by openCardSheet: the panel opens straight on this card's sheet. */
  card: SheetCard | null
  /** Bumped by every openCardSheet, so a second card (or the same card
   *  again) reaches a panel that is already open. */
  cardSeq: number
}

const CLOSED: SearchOpenState = { open: false, mode: 'find', card: null, cardSeq: 0 }
let state: SearchOpenState = CLOSED
const listeners = new Set<() => void>()

function set(next: SearchOpenState) {
  if (next.open === state.open && next.mode === state.mode && next.card === state.card && next.cardSeq === state.cardSeq) return
  state = next
  for (const l of listeners) l()
}

export function openSearch(mode: SearchMode = 'find'): void {
  set({ open: true, mode, card: null, cardSeq: state.cardSeq })
}

/**
 * Open the search panel directly on one card's full sheet (oracle text,
 * prices, the per-format legality grid). Used by surfaces outside the search,
 * e.g. the deck legality chips. Only meaningful while a SearchHost is
 * mounted (nav v2): check useSearchHostMounted() before offering it.
 */
export function openCardSheet(card: SheetCard): void {
  set({ open: true, mode: 'find', card, cardSeq: state.cardSeq + 1 })
}

export function closeSearch(): void {
  set({ ...state, open: false, card: null })
}

export function getSearchOpenState(): SearchOpenState {
  return state
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function useSearchOpenState(): SearchOpenState {
  return useSyncExternalStore(subscribe, getSearchOpenState, () => CLOSED)
}

// How many SearchHosts are mounted (HeaderV2 mounts one; flag-off navigation
// mounts none, and openCardSheet would then open nothing).
let hosts = 0
const hostListeners = new Set<() => void>()

/** Called by SearchHost on mount; returns the unregister. */
export function registerSearchHost(): () => void {
  hosts += 1
  for (const l of hostListeners) l()
  return () => {
    hosts -= 1
    for (const l of hostListeners) l()
  }
}

function subscribeHosts(listener: () => void): () => void {
  hostListeners.add(listener)
  return () => { hostListeners.delete(listener) }
}

/** True while a search panel host is mounted, i.e. openCardSheet will show. */
export function useSearchHostMounted(): boolean {
  return useSyncExternalStore(subscribeHosts, () => hosts > 0, () => false)
}

/** True when a keystroke there is the user typing (the `/` shortcut stays out). */
export function isTypingTarget(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') return true
  // eslint-disable-next-line i18next/no-literal-string -- CSS selector
  if (el instanceof HTMLElement && (el.isContentEditable || el.closest('[contenteditable=""], [contenteditable="true"]'))) return true
  return false
}

/** Test seam. */
export function __resetSearchOpen(): void {
  state = CLOSED
  for (const l of listeners) l()
}
